import { currencyDisplay } from "../../../../utils/helpers";
import { Addon, FareType, MoneyFormatter, PayMode } from "./types";

function sumBy(items: Addon[], key: "amount" | "currency_amount") {
  return items.reduce((total, item) => total + Number(item?.[key] ?? 0), 0);
}

interface Selection {
  fareType: FareType;
  payMode: PayMode;
  selectedAddonIds: number[];
}

// Every figure the dedicated payment page shows, derived from the new_payment
// payload and the customer's current selection.
//
// Unpaid add-ons are already part of `total_price` and `balance_amount`, so
// settling one reduces the remaining balance by exactly its amount.
export function usePaymentAmounts(paymentInfo: any, { fareType, payMode, selectedAddonIds }: Selection) {
  const currency = paymentInfo?.currency ?? "MYR";
  const money: MoneyFormatter = (myr, foreign) =>
    currencyDisplay(currency, Number((currency === "MYR" ? myr : foreign) ?? 0));

  const totalPrice = paymentInfo?.total_price ?? 0;
  const balanceAmount = paymentInfo?.balance_amount ?? 0;
  const currBalanceAmount = paymentInfo?.currency_balance_amount ?? 0;
  const deposit = paymentInfo?.deposit ?? 0;
  const currDeposit = paymentInfo?.currency_deposit ?? 0;
  const totalQuantity = paymentInfo?.total_quantity ?? 0;

  const addons: Addon[] = paymentInfo?.balance_revisions ?? [];
  const hasAddons = addons.length > 0;
  const selectableAddons = addons.filter((addon) => addon.payment_status !== "in_progress");
  const selectedAddons = addons.filter((addon) => selectedAddonIds.includes(addon.id));

  const addonsTotal = sumBy(addons, "amount");
  const currAddonsTotal = sumBy(addons, "currency_amount");
  const selectableTotal = sumBy(selectableAddons, "amount");
  const currSelectableTotal = sumBy(selectableAddons, "currency_amount");
  const selectedTotal = sumBy(selectedAddons, "amount");
  const currSelectedTotal = sumBy(selectedAddons, "currency_amount");

  const showDepositToggle = Boolean(paymentInfo?.allow_customer_pay_deposit && !paymentInfo?.deposit_covered);
  const payingDeposit = fareType === "customer_deposit" && showDepositToggle;
  const payingAddonsOnly = hasAddons && fareType === "customer_full_fare" && payMode === "selected_addons";

  // The design lists the tour fare on its own, with the unpaid add-ons broken
  // out beside it.
  const tourPrice = hasAddons ? totalPrice - addonsTotal : totalPrice;
  const balanceExAddons = balanceAmount - addonsTotal;
  const currBalanceExAddons = currBalanceAmount - currAddonsTotal;

  const payingNow = payingAddonsOnly ? selectedTotal
    : fareType === "customer_deposit" ? deposit : balanceAmount;
  const currPayingNow = payingAddonsOnly ? currSelectedTotal
    : fareType === "customer_deposit" ? currDeposit : currBalanceAmount;

  const balanceAfterPayment = balanceAmount - payingNow;
  const currBalanceAfterPayment = currBalanceAmount - currPayingNow;

  const depositPerPax = totalQuantity > 0 ? deposit / totalQuantity : 0;
  const currDepositPerPax = totalQuantity > 0 ? currDeposit / totalQuantity : 0;
  const remainingAfterDeposit = totalPrice - deposit;

  return {
    currency,
    money,
    totalQuantity,

    addons,
    hasAddons,
    selectableAddons,
    addonsTotal,
    currAddonsTotal,
    selectableTotal,
    currSelectableTotal,

    tourPrice,
    balanceAmount,
    currBalanceAmount,
    balanceExAddons,
    currBalanceExAddons,

    deposit,
    currDeposit,
    depositPerPax,
    currDepositPerPax,
    remainingAfterDeposit,

    payingNow,
    currPayingNow,
    balanceAfterPayment,
    currBalanceAfterPayment,

    showDepositToggle,
    payingDeposit,
    payingAddonsOnly,
    showPaymentTerms: Boolean(paymentInfo?.allow_customer_pay_deposit) || hasAddons,
    nothingSelected: payingAddonsOnly && selectedAddonIds.length === 0,
  };
}

export type PaymentAmounts = ReturnType<typeof usePaymentAmounts>;
